import { Grid } from '@mui/material';
import React from 'react';
import { Heading, Subheading } from '../../components/shared/typography/PrimaryTypography';
import QuantitativeDataCompletenessHeatmap from '../../components/metric_collection/data_completeness/QuantitativeDataCompletenessHeatmap';
import {
  MetricRecordEntitiesMap,
  getMetricRecordEntitiesMap
} from '../../graphql/services/metric_record.service';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { UserContext } from '../../context/UserContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import AccessDenied from '../shared/AccessDenied';
import { ReportingPeriodGroup } from '@esg/esg-global-types';
import ReportingPeriodGroupSelect from '../../components/shared/input/select/ReportingPeriodGroupSelect';
import { values } from '../../util/general_utils';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import PrimarySkeleton from '../../components/shared/loading/PrimarySkeleton';
import QualitativeDataCompletenessDatagrid from '../../components/metric_collection/data_completeness/QualitativeDataCompletenessDatagrid';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import GhostButton from '../../components/shared/button/GhostButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { FUNCTION_UNKNOWN_FAILURE, FEEDBACK_ACTION_FAILURE } from '../../util/global_variables';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';

const SOURCE = 'MetricCollectionDataCompleteness.tsx';

/**
 * View for displaying the captured metric record progress for quantitative and qualitative metrics.
 * @returns {JSX.Element}
 */
const MetricCollectionDataCompleteness = () => {
  const initial_entities_map: MetricRecordEntitiesMap = {
    countries: {},
    regions: {},
    divisions: {},
    subdivisions: {},
    sites: {},
    reporting_period_groups: {},
    reporting_periods: {},
    metrics: {},
    emission_factors: {},
    external_companies: {}
  };
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking('Sustainability', 'Data Completeness', group.id, company.id);

  const [entitiesMap, setEntitiesMap] =
    React.useState<MetricRecordEntitiesMap>(initial_entities_map);
  const [reportingPeriodGroup, setReportingPeriodGroup] =
    React.useState<ReportingPeriodGroup | null>(null);
  const [showDataCompleteness, setShowDataCompleteness] = React.useState<boolean>(false);
  const [loadingEntities, setLoadingEntities] = React.useState<boolean>(true);
  const [access, setAccess] = React.useState<ViewAccess>(null);
  const navigate: NavigateFunction = useNavigate();

  // Navigate to main metric collection view.
  const handleMetricCollectionClick = (): void => {
    navigate('/sustainability/metrics');
  };

  // Set a selected reporting period group to the capture parameter
  const handleSelectReportingPeriodGroup = (
    reporting_period_group: ReportingPeriodGroup | null
  ): void => {
    setReportingPeriodGroup(reporting_period_group);
    setShowDataCompleteness(false);
  };

  // Fetch entities needed for metric record data completeness displays.
  const fetchMetricRecordEntitiesMap = async () => {
    setLoadingEntities(true);
    try {
      if (group && company && user) {
        const entities_map: MetricRecordEntitiesMap = await getMetricRecordEntitiesMap(
          group.id,
          company.id,
          user
        );
        if (entities_map) {
          setEntitiesMap({
            countries: entities_map.countries,
            regions: entities_map.regions,
            divisions: entities_map.divisions,
            subdivisions: entities_map.subdivisions,
            sites: entities_map.sites,
            reporting_period_groups: entities_map.reporting_period_groups,
            reporting_periods: entities_map.reporting_periods,
            metrics: entities_map.metrics,
            emission_factors: entities_map.emission_factors,
            external_companies: entities_map.external_companies
          });
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchMetricRecordEntitiesMap'),
          {
            group: group,
            company: company,
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch metric record entities', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      setEntitiesMap(initial_entities_map);
      setReportingPeriodGroup(null);
      setShowDataCompleteness(false);
      if (group && company && user) {
        await fetchMetricRecordEntitiesMap();
      }
    })();
  }, [user, company, group]);

  React.useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'sustainability.metric_collection')
        : null
    );
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Grid container spacing={4} sx={{ textAlign: 'start' }}>
      <Grid size={12}>
        <Heading weight="Medium">Metric Capture Data Completeness</Heading>
      </Grid>
      <Grid size={4}>
        {loadingEntities ? (
          <PrimarySkeleton variant="rounded" height={50} />
        ) : (
          <ReportingPeriodGroupSelect
            reporting_period_group_options={values<ReportingPeriodGroup>(
              entitiesMap.reporting_period_groups
            )}
            selected_reporting_period_group={reportingPeriodGroup}
            handleChangeReportingPeriodGroups={(
              reporting_period_groups: ReportingPeriodGroup | Array<ReportingPeriodGroup> | null
            ) => {
              if (!Array.isArray(reporting_period_groups)) {
                handleSelectReportingPeriodGroup(reporting_period_groups);
              }
            }}
            input_label="Financial Year"
          />
        )}
      </Grid>
      <Grid size={3} sx={{ display: 'flex', alignItems: 'center' }}>
        <PrimaryButton
          id="view-data-button"
          disabled={!reportingPeriodGroup}
          onClick={() => setShowDataCompleteness(true)}
        >
          {`View Data Completeness`}
        </PrimaryButton>
      </Grid>
      <Grid size={12} sx={{ minHeight: '50vh' }}>
        {showDataCompleteness && reportingPeriodGroup && (
          <Grid container spacing={4}>
            <Grid size={12}>
              <Subheading sx={{ textAlign: 'left' }}>Numerical Data Completeness</Subheading>
            </Grid>
            <Grid size={12}>
              <QuantitativeDataCompletenessHeatmap
                reporting_period_group={reportingPeriodGroup}
                entities_map={entitiesMap}
              />
            </Grid>
            <Grid size={12}>
              <Subheading sx={{ textAlign: 'left' }}>Disclosures Data Completeness</Subheading>
            </Grid>
            <Grid size={12}>
              <QualitativeDataCompletenessDatagrid
                reporting_period_group={reportingPeriodGroup}
                entities_map={entitiesMap}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid size={12}>
        <GhostButton
          dark
          outlined
          startIcon={<ChevronLeftIcon />}
          onClick={handleMetricCollectionClick}
        >
          Metric Collection
        </GhostButton>
      </Grid>
    </Grid>
  ) : (
    <AccessDenied />
  );
};

export default MetricCollectionDataCompleteness;
